import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Sparkles, Loader2, Bot } from 'lucide-react';

const OPENAI_MODELS = ['gpt-5.5', 'gpt-5.4', 'gpt-5.4-mini', 'gpt-5.2', 'gpt-5.1', 'gpt-5', 'gpt-5-mini', 'gpt-5-nano'];
const ANTHROPIC_MODELS = ['claude-opus-4-8', 'claude-opus-4-7', 'claude-sonnet-4-6', 'claude-opus-4-6', 'claude-sonnet-4-5-20250929', 'claude-haiku-4-5-20251001'];

const ZippieSettingsCard = () => {
  const { api, user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    api.get('/zippie/settings')
      .then((r) => setSettings(r.data))
      .catch(() => toast.error('Failed to load Zippie settings'))
      .finally(() => setLoading(false));
  }, [api, isAdmin]);

  if (!isAdmin) return null;
  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
        </CardContent>
      </Card>
    );
  }
  if (!settings) return null;

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch('/zippie/settings', {
        provider: settings.provider,
        openai_model: settings.openai_model,
        anthropic_model: settings.anthropic_model,
      });
      setSettings(res.data);
      toast.success('Zippie AI provider updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="zippie-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Bot className="w-5 h-5 text-teal-500" /> Zippie AI provider</CardTitle>
        <CardDescription>Choose which LLM powers the Zippie assistant. All routes stay the same — you can toggle without a redeploy.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-sm font-medium">Provider</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              type="button"
              onClick={() => setSettings((s) => ({ ...s, provider: 'openai' }))}
              className={`p-3 rounded-lg border text-sm text-left transition-colors ${
                settings.provider === 'openai'
                  ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid="provider-openai"
            >
              <div className="font-semibold flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-emerald-500" /> OpenAI GPT</div>
              <p className="text-xs text-slate-500 mt-0.5">Fast, broad general-purpose model.</p>
            </button>
            <button
              type="button"
              onClick={() => setSettings((s) => ({ ...s, provider: 'anthropic' }))}
              className={`p-3 rounded-lg border text-sm text-left transition-colors ${
                settings.provider === 'anthropic'
                  ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid="provider-anthropic"
            >
              <div className="font-semibold flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-amber-500" /> Anthropic Claude</div>
              <p className="text-xs text-slate-500 mt-0.5">Warmer, more natural — great for kids & parents.</p>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>OpenAI model</Label>
            <select
              value={settings.openai_model}
              onChange={(e) => setSettings((s) => ({ ...s, openai_model: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
              disabled={settings.provider !== 'openai'}
              data-testid="openai-model-select"
            >
              {OPENAI_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <Label>Anthropic model</Label>
            <select
              value={settings.anthropic_model}
              onChange={(e) => setSettings((s) => ({ ...s, anthropic_model: e.target.value }))}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
              disabled={settings.provider !== 'anthropic'}
              data-testid="anthropic-model-select"
            >
              {ANTHROPIC_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="zippie-settings-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ZippieSettingsCard;
